import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { QuantityInput } from 'cat-ecommerce-alloy';
import RemanufacturedPartMessage from '@app/components/common/RemanufacturedPartMessage';
import styles from './styles.modules.scss';
import cx from 'classnames';
import {
  cartItemPropType,
  alternatePartsPropType
} from '../declarations.propTypes';
import UnitPriceTableCell from '../../common/UnitPriceTableCell';
import { getAvailability } from '../../common/commonUtils';
import { getQuantitiesError, getPartTypeValue } from '../utils';

const AlternativePartsTableMobileView = ({
  item,
  currency,
  alternateParts,
  quantities,
  setQuantity,
  availabilityTypeToggle,
  showQuantityError
}) => {
  const [t] = useTranslation();

  const isStoreAvailabilityEnabled = useSelector(
    s => s.dealer?.isStoreAvailabilityEnabled
  );
  const hoseAvailabilityLabel = useSelector(
    s => s.common?.hoseAvailabilityLabel
  );

  const availableAlternates = alternateParts?.map((element, index) => {
    const isRemanPart = element.partLevelFlags.isReman;
    return (
      <>
        <hr className={`${styles['hr-margin']}`} />
        {index === 0 && (
          <p className="ps-2 text-uppercase u-color--ExtraDarkGray text-univers--bold u-fs--16 ">
            {t('AVAILABLE_ALTERNATIVES')}
          </p>
        )}
        <div
          className={`d-flex flex-column ps-2 mt-2`}
          key={`alt-${element.referenceId}-${element.partName}`}
        >
          <p className="text-univers u-fs--16 u-color--ExtraDarkGray mb-1">{`${element.partNumber}: ${element.partName}`}</p>
          <QuantityInput
            className={cx('pb-2', styles['quantity-input'])}
            /**
             * Setting error instead of validator because we need to consider all QuantityInputs
             * in the tables and the validator only fires on changes of current QuantityInput
             * Note: must have a setter in order for error state to propogate to QuantityInput component
             */
            error={
              getQuantitiesError(quantities, t) ||
              showQuantityError(element, quantities[element.referenceId])
            }
            setError={() => null}
            value={quantities[element.referenceId]?.toString() ?? '0'}
            setValue={value =>
              setQuantity(element.referenceId, parseInt(value) ?? 0)
            }
          />
          <p className="d-flex flex-column align-items pb-2 mb-0 u-fs--14 u-color--DarkGray">
            {t('TYPE')}: {t(getPartTypeValue(element.partType))}
          </p>
          <>
            <p className="d-flex flex-column align-items start u-fs--16 u-color--ExtraDarkGray mt-1 mb-2">
              <UnitPriceTableCell
                unitPrice={element.price?.formatted?.unitPrice}
                coreCharges={element.price?.formatted?.coreCharges}
                showCoreCharges={!!element.price?.unformatted?.coreCharges}
                currency={currency}
              />
            </p>
            {getAvailability(
              element,
              isStoreAvailabilityEnabled,
              hoseAvailabilityLabel,
              availabilityTypeToggle
            )}
          </>
          {isRemanPart && <RemanufacturedPartMessage />}
        </div>
      </>
    );
  });

  const isReman = item.partLevelFlags.isReman;
  return (
    <div>
      <>
        <div
          className={`d-flex flex-column row ms-n2`}
          key={`${item?.id}-${item?.item}`}
        >
          <p className="text-univers u-fs--16 u-color--ExtraDarkGray mb-1">
            {item?.item}
          </p>
          <p className="text-univers u-fs--14 fw-bold u-color--DarkGray mb-1">
            {t('QUANTITY1')}
          </p>
          <QuantityInput
            className={styles['quantity-input']}
            /**
             * Setting error instead of validator because we need to consider all QuantityInputs
             * in the tables and the validator only fires on changes of current QuantityInput
             * Note: must have a setter in order for error state to propogate to QuantityInput component
             */
            error={
              getQuantitiesError(quantities, t) ||
              showQuantityError(item, quantities[item.referenceId])
            }
            setError={() => null}
            value={quantities[item.referenceId]?.toString() ?? '0'}
            setValue={value =>
              setQuantity(item.referenceId, parseInt(value) ?? 0)
            }
          />
          <p className="d-flex flex-column u-fs--16 u-color--ExtraDarkGray mt-1 mb-2">
            <UnitPriceTableCell
              unitPrice={item?.unitPrice}
              coreCharges={item.price?.formatted?.coreCharges}
              showCoreCharges={!!item.price?.unformatted?.coreCharges}
              currency={currency}
            />
          </p>
          {getAvailability(
            item,
            isStoreAvailabilityEnabled,
            hoseAvailabilityLabel,
            availabilityTypeToggle
          )}

          {isReman && <RemanufacturedPartMessage />}
        </div>
        {availableAlternates.length >= 1 ? availableAlternates : null}
      </>
    </div>
  );
};

AlternativePartsTableMobileView.propTypes = {
  item: cartItemPropType,
  alternateParts: alternatePartsPropType,
  currency: PropTypes.string,
  quantities: PropTypes.shape({}),
  setQuantity: PropTypes.func,
  showQuantityError: PropTypes.func,
  availabilityTypeToggle: PropTypes.bool
};
export default AlternativePartsTableMobileView;
